// Pure helpers for the estimate-style quote print (QuoteEstimatePrint).
// Groups the quote's material + labor rows by productStructureId and computes
// the Base Bid (primary flooring categories) and grand Total. Side-effect free
// so it can be unit-tested in isolation.
import {
  CARPET_ID_DB,
  SHEET_VINYL_ID_DB,
  VINYL_PLANK_ID_DB,
  WALLBASE_ID_DB,
  TRANSITION_ID_DB,
  INSTALLATION_ACCESSORIES_ID_DB,
} from "../../../_utils/constants/ProductCatalog";

// Category grouping mirrors the estimate table: the three flooring products and
// wall base each map to their own group, while transitions (171) and
// installation accessories (173) are sundries. Any unmapped id falls back to the
// "Wall Base" catch-all so every item still shows within a group.
export const WALL_BASE_CATEGORY = "Wall Base";
export const SUNDRIES_CATEGORY = "Sundries";
export const MISC_LABOR_CATEGORY = "Misc Labor";
export const CATEGORY_MAP = {
  [CARPET_ID_DB]: "Carpet", // 142
  [SHEET_VINYL_ID_DB]: "Sheet Vinyl", // 143
  [VINYL_PLANK_ID_DB]: "Vinyl Plank", // 168
  [WALLBASE_ID_DB]: WALL_BASE_CATEGORY, // 172
  [TRANSITION_ID_DB]: SUNDRIES_CATEGORY, // 171
  [INSTALLATION_ACCESSORIES_ID_DB]: SUNDRIES_CATEGORY, // 173
};

// Display order of the groups: flooring, then wall base, sundries, and finally
// the Misc Labor catch-all for standalone (non-kit) labor.
export const CATEGORY_NAME_ORDER = ["Carpet", "Sheet Vinyl", "Vinyl Plank", WALL_BASE_CATEGORY, SUNDRIES_CATEGORY, MISC_LABOR_CATEGORY];

// Categories that count toward the Base Bid (mirrors estimate PRIMARY_MATERIAL_CATEGORIES).
export const PRIMARY_MATERIAL_CATEGORIES = ["Carpet", "Sheet Vinyl", "Vinyl Plank"];

const num = (v) => parseFloat(v) || 0;

const kitIdOf = (row) => parseInt(row.kitId, 10) || 0;

const categoryNameFor = (productStructureId) =>
  CATEGORY_MAP[parseInt(productStructureId, 10)] || WALL_BASE_CATEGORY;

// The structure of the product a kit belongs to. The API labels this field
// either masterKitProductStructureId or kitProductStructureId depending on the
// endpoint, so prefer the former and fall back to the latter. (Real structure
// ids are 142+, so a 0/blank value counts as absent.)
const kitStructureOf = (row) =>
  (parseInt(row.masterKitProductStructureId, 10) || 0) > 0
    ? row.masterKitProductStructureId
    : row.kitProductStructureId;

// A kit row (kitId > 0) groups with its main product: the kit's own
// productStructureId is the kit's structure (e.g. 173), so the kit's product
// structure — the structure of the product the kit belongs to — drives the group
// instead. Non-kit materials group by their own productStructureId; non-kit labor
// is standalone and collects under Misc Labor.
const materialCategoryName = (row) =>
  kitIdOf(row) > 0
    ? categoryNameFor(kitStructureOf(row))
    : categoryNameFor(row.productStructureId);

const laborCategoryName = (row) =>
  kitIdOf(row) > 0
    ? categoryNameFor(kitStructureOf(row))
    : MISC_LABOR_CATEGORY;

const categorySortKey = (name) => {
  const idx = CATEGORY_NAME_ORDER.indexOf(name);
  return idx !== -1 ? idx : 999;
};

// Material label: style (+ color) first, private name only as fallback.
export function materialLineName(row) {
  const priv = row.privateName || row.pvtName || "";
  if (priv && priv !== "null") return priv;
  const style = row.product || row.style || "";
  const color = row.color || "";
  const styled = [style, color].filter(Boolean).join(" ").trim();
  if (styled) return styled;
  return "";
}

export function laborLineName(row) {
  return row.labor || row.laborName || "";
}

function toItem(name, row) {
  return {
    name,
    qty: num(row.quantity),
    price: num(row.unitPrice != null ? row.unitPrice : row.price),
    lineTotal: num(row.linePrice),
  };
}

// Ordered, non-empty category groups merging material + labor. Kit rows
// (kitId > 0) bucket by their main product (masterKitProductStructureId);
// non-kit materials bucket by their own productStructureId; non-kit labor
// collects under Misc Labor. Unmapped ids fall back to the "Wall Base" catch-all.
export function buildQuoteEstimateGroups({ materialListData = [], laborData = [] } = {}) {
  const buckets = new Map();

  const bucketByName = (name) => {
    if (!buckets.has(name)) {
      buckets.set(name, {
        name,
        primary: PRIMARY_MATERIAL_CATEGORIES.includes(name),
        sortKey: categorySortKey(name),
        items: [],
      });
    }
    return buckets.get(name);
  };

  (materialListData || []).forEach((row) =>
    bucketByName(materialCategoryName(row)).items.push(toItem(materialLineName(row), row))
  );
  (laborData || []).forEach((row) =>
    bucketByName(laborCategoryName(row)).items.push(toItem(laborLineName(row), row))
  );

  return Array.from(buckets.values())
    .filter((g) => g.items.length > 0)
    .sort((a, b) => a.sortKey - b.sortKey);
}

// Base Bid = primary-group line totals; Total = all-group line totals.
// lastPrimaryName anchors where the Base Bid row renders (null = no primary items).
export function computeQuoteEstimateTotals(groups = []) {
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  let baseBid = 0;
  let total = 0;
  let lastPrimaryName = null;
  groups.forEach((g) => {
    const groupTotal = g.items.reduce((s, it) => s + it.lineTotal, 0);
    total += groupTotal;
    if (g.primary) {
      baseBid += groupTotal;
      lastPrimaryName = g.name;
    }
  });
  return { baseBid: round2(baseBid), total: round2(total), lastPrimaryName };
}
